import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { useUpdateProductShelvingRequirement } from "../../hooks"
import { productShelvingRequirementFormSchema } from "../../types"
import type { ProductShelvingRequirement, ProductShelvingRequirementFormValues } from "../../types"

import ProductShelvingRequirementForm from "./ProductShelvingRequirementForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import type { ProductShelvingRequirementInput } from "@/graphql/codegen/graphql"

interface ProductShelvingRequirementFormDialogProps {
  productId: number
  shelvingRequirement: ProductShelvingRequirement | null
  onSubmit: () => void
  onClose: () => void
}

const ProductShelvingRequirementFormDialog: FC<ProductShelvingRequirementFormDialogProps> = ({
  productId,
  shelvingRequirement,
  onSubmit,
  onClose,
}) => {
  const isCreateMode = !shelvingRequirement
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const methods = useForm<ProductShelvingRequirementFormValues>({
    defaultValues: {
      coldStorage: shelvingRequirement?.coldStorage ?? null,
      h3ColdStorage: shelvingRequirement?.h3ColdStorage ?? null,
      binSize: shelvingRequirement?.binSize ?? "",
      deep: shelvingRequirement?.coldStorage ?? null,
      stackable: shelvingRequirement?.stackable ?? null,
    },
    resolver: zodResolver(productShelvingRequirementFormSchema),
    mode: "all",
  })
  const {
    formState: { isDirty, isValid },
    handleSubmit,
  } = methods
  const { update, isUpdating } = useUpdateProductShelvingRequirement({
    ...(isCreateMode
      ? {
          errorMessage: "Failed to add product shelving requirements.",
          successMessage: "Product shelving requirements successfully added.",
        }
      : {
          errorMessage: "Failed to update product shelving requirements.",
          successMessage: "Product shelving requirements successfully updated.",
        }),
    onSuccess: onSubmit,
  })
  const canSubmit = isDirty && isValid && !isUpdating

  const handleClose = () => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }

  const handleCancelDialogClose = () => setIsCancelDialogOpen(false)

  const submitForm = async (formValues: ProductShelvingRequirementFormValues) => {
    await update({ productId, ...formValues } as ProductShelvingRequirementInput)
  }

  return isCancelDialogOpen ? (
    <CancelDialog onClose={handleCancelDialogClose} onConfirm={onClose} />
  ) : (
    <Dialog open fullWidth onClose={handleClose} data-testid='shelving-requirement-form-dialog'>
      <DialogTitle className='pl-10 font-normal text-primary'>
        {isCreateMode ? "Add" : "Edit"} Shelving Requirements
      </DialogTitle>
      <IconButton className='absolute right-4 top-3' aria-label='Close' onClick={handleClose}>
        <CloseOutlined className='text-blue-gray' />
      </IconButton>
      <Divider />
      <DialogContent className='p-10'>
        <FormProvider {...methods}>
          <ProductShelvingRequirementForm />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button data-testid='cancel-button' appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button data-testid='submit-button' variant='primary' disabled={!canSubmit} onClick={handleSubmit(submitForm)}>
          {isCreateMode ? "Save" : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ProductShelvingRequirementFormDialog
